import { useMemo } from 'react';

import { useTranslate } from '@src/core/hooks';
import { PaymentMethodCode, type PaymentOption } from '@src/domains/payments';
import { resolveTransactionError, TransactionErrorKind } from '@src/domains/transactions';
import { usePaymentErrors } from '@src/features/payments/errors/hooks/usePaymentErrors';

export type PaymentErrorDialogCopy = {
  kind: TransactionErrorKind;
  title: string;
  body: string;
  primaryLabel: string;
  secondaryLabel: string | null;
  canPayWithPix: boolean;
};

const COPY = {
  [TransactionErrorKind.Declined]: {
    title: 'checkout_payment_error_declined_title',
    body: 'checkout_payment_error_declined_body',
    pix: 'checkout_payment_error_declined_body_pix',
  },
  [TransactionErrorKind.Issuer]: {
    title: 'checkout_payment_error_issuer_title',
    body: 'checkout_payment_error_issuer_body',
    pix: 'checkout_payment_error_issuer_body_pix',
  },
  [TransactionErrorKind.Blocked]: {
    title: 'checkout_payment_gateway_blocked_buyer_error_title',
    body: 'checkout_payment_gateway_blocked_buyer_error_helper_text',
    pix: null,
  },
  [TransactionErrorKind.Gateway]: {
    title: 'checkout_payment_gateway_mismatch_error_title',
    body: 'checkout_payment_gateway_mismatch_error_description',
    pix: null,
  },
} as const;

const readOptional = (value: string, key: string) => (value === key ? '' : value);

// `refused` is the method the buyer just tried. Pix is only a way out of someone else's failure:
// offering it to a buyer whose pix is what failed sends them back into the same error.
export const usePaymentErrorDialog = (options: PaymentOption[], refused?: PaymentOption) => {
  const { i18n } = useTranslate();
  const { errors, clear } = usePaymentErrors();

  const kind = resolveTransactionError(errors);
  const sellerTakesPix = options.some((option) => option.paymentMethod.code === PaymentMethodCode.Pix);
  const hasPix = sellerTakesPix && refused?.paymentMethod.code !== PaymentMethodCode.Pix;

  const dialog = useMemo<PaymentErrorDialogCopy | null>(() => {
    if (!kind) {
      return null;
    }

    const copy = COPY[kind];
    const body = i18n(copy.body);

    if (!copy.pix) {
      return {
        kind,
        title: i18n(copy.title),
        body,
        primaryLabel: i18n('checkout_payment_gateway_mismatch_error_ok'),
        secondaryLabel: null,
        canPayWithPix: false,
      };
    }

    const pixSentence = readOptional(i18n(copy.pix), copy.pix);

    return {
      kind,
      title: i18n(copy.title),
      body: hasPix && pixSentence !== '' ? `${body} ${pixSentence}` : body,
      primaryLabel: i18n('checkout_payment_error_retry'),
      secondaryLabel: hasPix ? i18n('checkout_payment_error_secondary_pix') : null,
      canPayWithPix: hasPix,
    };
  }, [kind, hasPix, i18n]);

  return { dialog, dismiss: clear };
};
